import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { LayoutDashboard, Waves, FlaskConical, Users, LogOut, Menu, X, Languages, Mail, CreditCard, Phone } from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navigation = [
    { name: t('dashboard'), href: '/', icon: LayoutDashboard },
    { name: t('pools'), href: '/pools', icon: Waves },
    { name: 'Subscription', href: '/subscription', icon: CreditCard },
  ];

  if (user?.role === 'admin') {
    navigation.push({ name: t('users'), href: '/users', icon: Users });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                data-testid="mobile-menu-button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-foreground hover:bg-accent"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <div className="flex items-center space-x-2 ml-2 lg:ml-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_93593000-b3be-431f-a37f-02f4cf188c0a/artifacts/x5wz1hkl_img_1_1724980327806.jpg" 
                  alt="AquaPro Logs" 
                  className="h-8"
                />
                <h1 className="text-xl font-bold text-secondary">AquaPro Logs</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                data-testid="language-toggle"
                onClick={toggleLanguage}
                variant="ghost"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Languages className="h-4 w-4" />
                <span className="text-sm font-medium">{i18n.language.toUpperCase()}</span>
              </Button>
              <Button
                data-testid="contact-admin-button"
                onClick={() => window.location.href = 'mailto:aquaprobcs@gmail.com?subject=AquaPro Logs Support Request'}
                variant="ghost"
                size="sm"
                className="flex items-center space-x-2"
                title="Contact Admin"
              >
                <Mail className="h-4 w-4" />
                <span className="text-sm font-medium hidden md:inline">Contact</span>
              </Button>
              <div className="flex items-center space-x-2">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-secondary">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
                <Button
                  data-testid="logout-button"
                  onClick={logout}
                  variant="ghost"
                  size="sm"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <nav
            className={`${
              sidebarOpen ? 'block' : 'hidden'
            } lg:block lg:w-64 space-y-2 bg-white p-4 rounded-lg border border-border`}
          >
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.href}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-accent'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
            
            {/* Contact Phone Panel */}
            <div className="mt-6 pt-4 border-t border-border">
              <a
                href="tel:979-446-1262"
                className="flex items-start space-x-3 px-4 py-3 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <Phone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">Have Swimming Pool Questions?</p>
                  <p className="text-sm font-semibold text-primary">Call us at 979-446-1262</p>
                </div>
              </a>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;